'use client'

import { useEffect, useState } from 'react'

// React hook that mirrors `body.cart-modal-open` for components that
// need to react to the cart modal opening/closing (e.g. the chat
// button / panel / mascot sliding left to make room for the Sheet).
//
// The cart modal itself is the source of truth — it toggles the body
// class AND dispatches `cart-modal-open` / `cart-modal-close` events
// (see src/components/Cart/CartModal.tsx). This hook listens to the
// events so we don't poll the DOM, and stays in sync with the CSS
// state because both are driven from the same `isOpen` toggle in
// CartModal. See docs/chatbot-sprite/19.
export function useCartModalOpen(): boolean {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    // Seed from the body class so a component that mounts after the
    // cart is already open still picks up the right state on first
    // render. Defensive: in practice the cart is opened by user
    // action long after the chat is mounted, but a cold start with
    // a deep link to /cart should work too.
    if (typeof document !== 'undefined' && document.body.classList.contains('cart-modal-open')) {
      setOpen(true)
    }
    const onOpen = () => setOpen(true)
    const onClose = () => setOpen(false)
    window.addEventListener('cart-modal-open', onOpen)
    window.addEventListener('cart-modal-close', onClose)
    return () => {
      window.removeEventListener('cart-modal-open', onOpen)
      window.removeEventListener('cart-modal-close', onClose)
    }
  }, [])
  return open
}

// Same width the Radix Sheet uses (`w-3/4 sm:max-w-md` in
// src/components/ui/sheet.tsx), plus a 24px breathing gap so the chat
// button doesn't kiss the cart's left edge. Exposed as a constant so
// every chat element that shifts stays in lock-step.
export const CART_MODAL_OFFSET_PX = 488 // 24 (gap) + 448 (Sheet max-w-md) + 16 (extra)
